import React, { useState, useEffect } from 'react';
import { adminSettingsAPI } from '../services/api';
import { HelpCircle } from 'lucide-react';

const FAQPage: React.FC = () => {
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await adminSettingsAPI.getCitiesAndCoverage();
        setCities(response.data.cities || []);
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  const faqs = [
    {
      question: 'How can I hire a driver through AllAvail?',
      answer: `You can book a professional driver through the AllAvail website or mobile app by selecting your travel requirements and preferred duration. ${cities.length > 0 ? `Our driver service is available in ${cities.join(', ')} and nearby areas.` : 'Our driver service is available in major cities throughout the region.'}`
    },
    {
      question: 'Are AllAvail drivers verified?',
      answer: 'Yes, all drivers undergo KYC verification, police verification, and background checks to ensure your safety and peace of mind.'
    },
    {
      question: 'Do you provide drivers near me?',
      answer: cities.length > 0
        ? `AllAvail offers driver services in ${cities.join(', ')} and connects customers with nearby available drivers based on your location.`
        : 'AllAvail offers driver services in multiple cities and connects customers with nearby available drivers based on your location.'
    },
    {
      question: 'Can I book a driver for elderly passengers?',
      answer: 'Yes, we provide trained drivers experienced in assisting elderly passengers safely and comfortably. Our drivers are trained to handle special requirements and ensure a comfortable journey.'
    },
    {
      question: 'What types of driver services do you offer?',
      answer: `We offer personal car drivers, drivers for elderly passengers, temporary drivers, outstation drivers, night-time drivers, and daily or monthly driver service packages. ${cities.length > 0 ? `All services are available in ${cities.join(', ')}.` : 'All services are available in major cities across the region.'}`
    },
    {
      question: 'How do I pay for driver services?',
      answer: 'AllAvail offers transparent pricing with no hidden charges. Payment can be made through our secure payment gateway after booking confirmation. We accept multiple payment methods for your convenience.'
    },
    {
      question: 'Can I book a driver for multiple days?',
      answer: 'Yes, AllAvail offers flexible booking options for one-way and round trips, including daily and monthly driver service packages. You can book drivers for hourly, daily, or multi-day trips based on your requirements.'
    },
    {
      question: 'What safety measures are in place?',
      answer: 'All drivers undergo comprehensive background checks, KYC verification, and police verification. We also provide real-time tracking and OTP verification for added safety during your journey.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-4">
            <HelpCircle className="h-8 w-8 text-black mr-3" />
            <h1 className="text-3xl md:text-4xl font-bold text-black">
              Frequently Asked Questions – Driver Services
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            Find answers to common questions about our professional driver services
          </p>
        </div>

        {/* FAQ Content */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <h2 className="text-xl md:text-2xl font-semibold text-black mb-3">
                  {faq.question}
                </h2>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Contact Section */}
        <div className="mt-12 bg-gray-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-semibold text-black mb-4">
            Still have questions?
          </h2>
          <p className="text-gray-600 mb-6">
            If you couldn't find the answer you're looking for, please contact our support team.
          </p>
          <a
            href="/login"
            className="inline-block bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;


